import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import TimeService from "src/services/timeService/timeService";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { Subscription } from "@dashboard/devices/types";
import { SubscriptionAddons } from "@dashboard/shared/subscription/SubscriptionAddons";
import { SubscriptionLimits } from "@dashboard/shared/subscription/SubscriptionLimits";
import { useCancelSubscriptionMutation } from "@dashboard/workspaces/api";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useSubscriptionExpiringWithinMonth,
} from "@dashboard/workspaces/api/helpers";
import { Box, DialogContentText } from "@mui/material";
import { DangerOutlinedButton, PrimaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { SectionField, SectionFields } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { SubscriptionAlert } from "../SubscriptionAlert/SubscriptionAlert";

type Props = {
    subscription: Subscription;
    features: API.Feature[];
    isLoading: boolean;
};

export const SubscriptionSection = (props: Props) => {
    const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = React.useState(false);
    const { t } = useTranslation(["modals", "pages", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();
    const mayCancelSubscription = workspaceAbility(workspace).can("cancel", "Workspace");
    const isExpired = useCurrentSubscriptionExpired();
    const isExpiringWithinMonth = useSubscriptionExpiringWithinMonth();
    const [cancelSubscription] = useCancelSubscriptionMutation();

    const confirmCancelSubscription = async (id: number) => {
        await cancelSubscription(id)
            .unwrap()
            .then(() => {
                showSuccessToast(t("pages:workspacePage.toast.subscriptionCancelSuccess"));
            })
            .finally(() => setCancelSubscriptionDialogOpen(false));
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
            <SubscriptionAlert />

            <SectionPanel
                title={t("pages:workspacePage.section.title.subscriptionOverview")}
                actions={
                    <>
                        <DangerOutlinedButton
                            data-testid="subscriptionSection.btn.cancel"
                            startIcon={<i className="fas fa-ban" />}
                            onClick={() => setCancelSubscriptionDialogOpen(true)}
                            disabled={!mayCancelSubscription || props.subscription.isCancelled}
                            tooltipProps={{
                                hide: mayCancelSubscription && !props.subscription.isCancelled,
                                title: props.subscription.isCancelled
                                    ? t("pages:workspacePage.tooltip.subscriptionCanceled")
                                    : t("pages:workspacePage.tooltip.noPermissionToCancelSubscription"),
                            }}
                        >
                            {t("pages:workspacePage.button.cancelSubscription")}
                        </DangerOutlinedButton>
                        {isExpired || isExpiringWithinMonth ? (
                            <PrimaryButton
                                data-testid="subscriptionSection.btn.renew"
                                onClick={() => window.open("https://emteria.com/p/subscription", "_blank")}
                            >
                                {t("pages:workspacePage.button.renewSubscription")}
                            </PrimaryButton>
                        ) : null}
                    </>
                }
            >
                <SectionFields>
                    <SectionField label={t("pages:workspacePage.tile.badge.status")} value={props.subscription.status} />
                    <SectionField
                        label={t("pages:workspacePage.tile.badge.chargeId")}
                        value={props.subscription.chargeId || "-"}
                    />
                    <SectionField
                        label={t("common:label.expirationDate")}
                        value={
                            props.subscription.expirationDate
                                ? TimeService.getLocalDate(props.subscription.expirationDate, "ISO", "date")
                                : "-"
                        }
                    />
                    <SectionField
                        label={t("pages:workspacePage.tile.badge.cancellationDate")}
                        value={
                            props.subscription.cancellationDate
                                ? TimeService.getLocalDate(props.subscription.cancellationDate, "ISO", "date")
                                : "-"
                        }
                    />
                </SectionFields>
            </SectionPanel>

            <SectionPanel
                title={t("common:subscription.addons")}
                actions={
                    <PrimaryButton onClick={() => window.open("https://emteria.com/p/subscription", "_blank")}>
                        {t("pages:workspacePage.button.purchaseAddons")}
                    </PrimaryButton>
                }
            >
                {props.isLoading ? (
                    <LoadingBox height={80} />
                ) : (
                    <SubscriptionAddons features={props.features} editable={false} variant="bare" />
                )}
            </SectionPanel>

            <SectionPanel
                title={t("common:subscription.limits")}
                actions={
                    <PrimaryButton onClick={() => window.open("https://emteria.com/p/subscription", "_blank")}>
                        {t("pages:workspacePage.button.purchaseLimitIncrease")}
                    </PrimaryButton>
                }
            >
                {props.isLoading ? (
                    <LoadingBox height={160} />
                ) : (
                    <SubscriptionLimits features={props.features} editable={false} variant="bare" />
                )}
            </SectionPanel>

            <ConfirmDialog
                title={t("modals:cancelSubscription.title")}
                open={cancelSubscriptionDialogOpen}
                onConfirm={() => confirmCancelSubscription(props.subscription.id)}
                onClose={() => setCancelSubscriptionDialogOpen(false)}
                dangerButton
            >
                <DialogContentText>{t("modals:cancelSubscription.description")}</DialogContentText>
            </ConfirmDialog>
        </Box>
    );
};
